import { useEffect, useState } from "react";
import { api, type Company, type Movement } from "../api";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";
import SwipeableRow from "../components/SwipeableRow";

const PAGE_SIZE = 50;

function formatTime(iso: string) {
  const date = new Date(iso);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function History({
  company,
  onBack,
  onMovementDeleted,
}: {
  company?: Company;
  onBack: () => void;
  onMovementDeleted: () => void;
}) {
  const [movements, setMovements] = useState<Movement[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Movement | null>(null);

  const confirmDelete = async (pin: string) => {
    if (!pendingDelete) return;
    await api.undo(pendingDelete.id, pin);
    setMovements((rows) => rows?.filter((m) => m.id !== pendingDelete.id) ?? null);
    setPendingDelete(null);
    onMovementDeleted();
  };

  useEffect(() => {
    api
      .movements({ companyId: company?.id, limit: PAGE_SIZE })
      .then((rows) => {
        setMovements(rows);
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch(() => setMovements([]));
  }, [company?.id]);

  const loadMore = async () => {
    if (!movements?.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const rows = await api.movements({
        companyId: company?.id,
        beforeId: movements[movements.length - 1]!.id,
        limit: PAGE_SIZE,
      });
      setMovements([...movements, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader title={company ? company.name : "History"} onBack={onBack} />

      {movements === null && (
        <p className="py-8 text-center font-semibold text-ink/50">Loading…</p>
      )}
      {movements?.length === 0 && (
        <p className="py-8 text-center font-semibold text-ink/50">
          Nothing registered yet{company ? ` for ${company.name}` : ""}.
        </p>
      )}

      {!!movements?.length && (
        <ul className="flex flex-col gap-2">
          {movements.map((movement) => (
            <li key={movement.id}>
              <SwipeableRow onDelete={() => setPendingDelete(movement)}>
                <div className="flex items-center gap-4 rounded-[14px] bg-white px-4 py-3 border border-line-soft shadow-card">
                  <span
                    className={`flex size-11 shrink-0 items-center justify-center rounded-full text-base font-bold tracking-[-0.02em] ${
                      movement.direction === "in" ? "bg-blush text-rose-ink" : "bg-lemon text-honey"
                    }`}
                  >
                    {movement.direction === "in" ? `+${movement.count}` : `−${movement.count}`}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{movement.companyName}</div>
                    <div className="text-xs font-semibold text-ink/50">
                      {movement.direction === "in" ? "Delivered" : "Picked up"} {movement.count}{" "}
                      {movement.count === 1 ? "box" : "boxes"} by {movement.driverName} ·{" "}
                      {formatTime(movement.createdAt)}
                    </div>
                  </div>
                </div>
              </SwipeableRow>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mx-auto rounded-full bg-white px-6 py-3 font-bold disabled:opacity-60 border border-line"
        >
          {loadingMore ? "Loading…" : "Show more"}
        </button>
      )}

      {pendingDelete && (
        <PinDialog
          title="Delete registration?"
          detail={`${pendingDelete.direction === "in" ? "Delivered" : "Picked up"} ${pendingDelete.count} ${
            pendingDelete.count === 1 ? "box" : "boxes"
          } — ${pendingDelete.companyName}, by ${pendingDelete.driverName}. Enter the PIN to delete.`}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
